import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaSave,
  FaUndo,
  FaArrowLeft,
  FaPercent,
  FaDollarSign,
  FaExclamationCircle,
} from "react-icons/fa";
import {
  useCreateFee,
  useFee,
  useUpdateFee,
} from "../../hooks/useSubscribtionDashboard";

const CreateFlightFee = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  console.log("🚀 ~ CreateFlightFee ~ id:", id);
  const { mutate: createFee, isPending: isCreating } = useCreateFee();
  const { mutate: updateFee, isLoading: isUpdating } = useUpdateFee();
  const isEditMode = !!id;

  const {
    data: feeData,
    isLoading: loadingFee,
    isError: isFeeError,
    error: feeError,
  } = useFee(id, {
    enabled: isEditMode, // Only fetch when we have an id
  });
  console.log("🚀 ~ CreateFlightFee ~ feeData:", feeData);

  const [formData, setFormData] = useState({
    internationalFlightTicketingFee: { method: "Fixed", value: 0 },
    localFlightTicketingFee: { method: "Fixed", value: 0 },
    regionalFlightTicketingFee: { method: "Fixed", value: 0 },
    apiTicketingFee: { method: "Fixed", value: 0 },
    extraFee: { method: "Fixed", value: 0 },
    rebookFee: { method: "Fixed", value: 0 },
    voidFee: { method: "Fixed", value: 0 },
    reIssueFee: { method: "Fixed", value: 0 },
    nameChangeFee: { method: "Fixed", value: 0 },
    changePassengerDetailFee: { method: "Fixed", value: 0 },
    cancelationFee: { method: "Fixed", value: 0 },
    fullRefundFee: { method: "Fixed", value: 0 },
    partialRefundFee: { method: "Fixed", value: 0 },
    bankTransferFee: { method: "Fixed", value: 0 },
    whiteLabelOnetimeFee: { method: "Fixed", value: 0 },
    whiteLabelMonthlySubscriptionFee: { method: "Fixed", value: 0 },
    invoiceSubscriptionFee: { method: "Fixed", value: 0 },
    SMSNotificationFee: { method: "Fixed", value: 0 },
    whatsappNotificationFee: { method: "Fixed", value: 0 },
    cardFee: { method: "Fixed", value: 0 },
    minimumFlightIssued: 0,
    maximumFlightIssued: 0,
    description: "",
    isDefault: false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Populate form with fetched data when in edit mode
  useEffect(() => {
    if (isEditMode && feeData) {
      setFormData(feeData?.data);
    }
  }, [isEditMode, feeData]);

  // Reset form when not in edit mode
  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode]);

  // Fee labels mapping
  const feeLabels = {
    internationalFlightTicketingFee: "International Flight Ticketing",
    localFlightTicketingFee: "Local Flight Ticketing",
    regionalFlightTicketingFee: "Regional Flight Ticketing",
    apiTicketingFee: "API Ticketing",
    extraFee: "Extra Fee",
    rebookFee: "Rebook",
    voidFee: "Void",
    reIssueFee: "Re-issue",
    nameChangeFee: "Name Change",
    changePassengerDetailFee: "Change Passenger Detail",
    cancelationFee: "Cancellation",
    fullRefundFee: "Full Refund",
    partialRefundFee: "Partial Refund",
    bankTransferFee: "Bank Transfer",
    whiteLabelOnetimeFee: "White Label One-time",
    whiteLabelMonthlySubscriptionFee: "White Label Monthly Subscription",
    invoiceSubscriptionFee: "Invoice Subscription",
    SMSNotificationFee: "SMS Notification",
    whatsappNotificationFee: "WhatsApp Notification",
    cardFee: "Card Fee",
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Only validate description field
    if (!formData.description || formData.description.trim() === "") {
      newErrors.description = "Description is required";
    }

    return newErrors;
  };

  // Handle field blur for validation
  const handleBlur = (field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  // Handle method change for regular fees
  const handleMethodChange = (feeName, method) => {
    setFormData((prev) => {
      // If changing to Fixed, only keep value
      if (method === "Fixed") {
        return {
          ...prev,
          [feeName]: {
            method: "Fixed",
            value: prev[feeName]?.value || 0,
          },
        };
      }

      // If changing to Percentage, add minCharge and maxCharge with defaults
      if (method === "Percentage") {
        return {
          ...prev,
          [feeName]: {
            method: "Percentage",
            value: prev[feeName]?.value || 0,
            minCharge: prev[feeName]?.minCharge || 0,
            maxCharge: prev[feeName]?.maxCharge || 2000,
          },
        };
      }

      return prev;
    });
  };

  // Handle value change for regular fees
  const handleValueChange = (feeName, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [feeName]: {
        ...prev[feeName],
        [field]: value === "" ? "" : parseFloat(value) || 0,
      },
    }));
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      internationalFlightTicketingFee: { method: "Fixed", value: 0 },
      localFlightTicketingFee: { method: "Fixed", value: 0 },
      regionalFlightTicketingFee: { method: "Fixed", value: 0 },
      apiTicketingFee: { method: "Fixed", value: 0 },
      extraFee: { method: "Fixed", value: 0 },
      rebookFee: { method: "Fixed", value: 0 },
      voidFee: { method: "Fixed", value: 0 },
      reIssueFee: { method: "Fixed", value: 0 },
      nameChangeFee: { method: "Fixed", value: 0 },
      changePassengerDetailFee: { method: "Fixed", value: 0 },
      cancelationFee: { method: "Fixed", value: 0 },
      fullRefundFee: { method: "Fixed", value: 0 },
      partialRefundFee: { method: "Fixed", value: 0 },
      bankTransferFee: { method: "Fixed", value: 0 },
      whiteLabelOnetimeFee: { method: "Fixed", value: 0 },
      whiteLabelMonthlySubscriptionFee: { method: "Fixed", value: 0 },
      invoiceSubscriptionFee: { method: "Fixed", value: 0 },
      SMSNotificationFee: { method: "Fixed", value: 0 },
      whatsappNotificationFee: { method: "Fixed", value: 0 },
      cardFee: { method: "Fixed", value: 0 },
      minimumFlightIssued: 0,
      maximumFlightIssued: 0,
      description: "",
      isDefault: false,
    });
    setErrors({});
    setTouched({});
  };

  // Save configuration
  const handleSave = () => {
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Mark all fields as touched to show errors
      const allTouched = {};
      Object.keys(validationErrors).forEach((field) => {
        allTouched[field] = true;
      });
      setTouched((prev) => ({ ...prev, ...allTouched }));
      return;
    }

    // Clean up the data before saving - remove unnecessary fields based on method
    const cleanedData = {
      ...formData,
      minimumFlightIssued: Number(formData?.minimumFlightIssued),
      maximumFlightIssued: Number(formData?.maximumFlightIssued),
    };

    // For each fee, ensure only relevant fields are included
    Object.keys(cleanedData).forEach((key) => {
      if (typeof cleanedData[key] === "object" && cleanedData[key] !== null) {
        const fee = cleanedData[key];

        if (fee.method === "Fixed") {
          // For Fixed method, only keep method and value
          cleanedData[key] = {
            method: "Fixed",
            value: Number(fee.value),
          };
        } else if (fee.method === "Percentage") {
          // For Percentage method, ensure minCharge and maxCharge exist
          cleanedData[key] = {
            method: "Percentage",
            value: Number(fee.value),
            minCharge: Number(fee.minCharge) || 0,
            maxCharge: Number(fee.maxCharge) || 2000,
          };
        }
      }
    });

    // If in edit mode, include the id
    if (isEditMode) {
      cleanedData.id = id;

      updateFee(cleanedData, {
        onSuccess: () => {
          resetForm();
          navigate("/subscritionDashboard/service-fees");
        },
        onError: (error) => {
          console.error("Error saving fee:", error);
        },
      });
    } else {
      createFee(cleanedData, {
        onSuccess: () => {
          resetForm();
          navigate("/subscritionDashboard/service-fees");
        },
        onError: (error) => {
          console.error("Error saving fee:", error);
        },
      });
    }
  };

  // Unified fee input renderer for all fees including cardFee
  const renderFeeInput = (feeName) => {
    const fee = formData[feeName];
    const isPercentage = fee?.method === "Percentage";

    return (
      <div
        key={feeName}
        className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
      >
        <label className="block text-sm font-semibold text-gray-700 mb-3 border-b pb-2">
          {feeLabels[feeName]}
        </label>
        <div className="space-y-4">
          {/* Method Field */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Method
            </label>
            <select
              value={fee?.method || "Fixed"}
              onChange={(e) => handleMethodChange(feeName, e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Fixed">Fixed</option>
              <option value="Percentage">Percentage</option>
            </select>
          </div>

          {/* Value Field */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Value
            </label>
            <div className="relative">
              <input
                type="number"
                value={fee?.value}
                onChange={(e) =>
                  handleValueChange(feeName, "value", e.target.value)
                }
                placeholder="Enter value"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 pl-8"
              />
              <span className="absolute left-2 top-2 text-gray-500">
                {isPercentage ? (
                  <FaPercent size={14} />
                ) : (
                  <FaDollarSign size={14} />
                )}
              </span>
            </div>
          </div>

          {/* Min Charge and Max Charge Fields - only for Percentage method */}
          {isPercentage && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Minimum Charge
                </label>
                <input
                  type="number"
                  value={fee?.minCharge}
                  onChange={(e) =>
                    handleValueChange(feeName, "minCharge", e.target.value)
                  }
                  placeholder="Enter minimum charge"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Maximum Charge
                </label>
                <input
                  type="number"
                  value={fee?.maxCharge || 2000}
                  onChange={(e) =>
                    handleValueChange(feeName, "maxCharge", e.target.value)
                  }
                  placeholder="Enter maximum charge"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 italic">
                Note: For percentage fees, the actual charge will be calculated
                within min-max range
              </p>
            </>
          )}
        </div>
      </div>
    );
  };

  // Show loading state when fetching fee data in edit mode
  if (isEditMode && loadingFee) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading fee configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if fetching fails
  if (isEditMode && isFeeError) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <FaExclamationCircle className="text-red-500 text-4xl mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-700 mb-2">
            Error Loading Fee
          </h2>
          <p className="text-red-600">
            {feeError?.message || "Failed to load fee configuration"}
          </p>
          <button
            onClick={() => navigate("/subscription-dashboard/service-fees")}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <FaArrowLeft className="text-gray-600" />
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
          {isEditMode
            ? "Edit service Fee Configuration"
            : "Create service Fee Configuration"}
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {Object.keys(feeLabels).map((feeName) => renderFeeInput(feeName))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Minimum Flight Issued
            </label>
            <input
              type="number"
              name="minimumFlightIssued"
              value={formData.minimumFlightIssued}
              onChange={handleChange}
              placeholder="Enter min flights"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Maximum Flight Issued
            </label>
            <input
              type="number"
              name="maximumFlightIssued"
              value={formData.maximumFlightIssued}
              onChange={handleChange}
              placeholder="Enter max flights"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200 md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                onBlur={() => handleBlur("description")}
                placeholder="Enter description (required)"
                className={`w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  touched.description && errors.description
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300"
                }`}
              />
              {touched.description && errors.description && (
                <div className="absolute right-2 top-2 text-red-500">
                  <FaExclamationCircle />
                </div>
              )}
            </div>
            {touched.description && errors.description && (
              <p className="mt-1 text-xs text-red-500">{errors.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center mb-6 p-4 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            name="isDefault"
            checked={formData.isDefault}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm font-medium text-gray-700">
            Set as Default Configuration
          </label>
        </div>

        <div className="flex flex-wrap gap-3 border-t pt-6">
          <button
            onClick={handleSave}
            disabled={isCreating || isUpdating}
            className="inline-flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaSave className="mr-2" />
            {isCreating
              ? "Saving..."
              : isUpdating
                ? "Update Configuration"
                : "Save Configuration"}
          </button>
          <button
            onClick={resetForm}
            disabled={isCreating}
            className="inline-flex items-center px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaUndo className="mr-2" />
            Reset Form
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateFlightFee;
